import { useTranslation } from 'react-i18next';

import { BACKEND_ERROR_CODE_TRANSLATIONS, BackendErrorCode } from '@/modules/common/api/errors';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import {
  BILL_SYNC_ERROR_I18N_KEYS,
  BILL_SYNC_ERROR_REASON_I18N_KEYS,
} from '../../constants/bill-sync-error-i18n';
import { BillListItem, BillSyncError, BillSyncOperation } from '../../types';

interface BillErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  bill?: BillListItem | null;
  syncOperation?: BillSyncOperation | null;
  syncError?: BillSyncError | null;
  onRetry?: () => void;
  isRetrying?: boolean;
}

export const BillErrorModal = ({
  isOpen,
  onClose,
  bill,
  onRetry,
  isRetrying = false,
  syncOperation = null,
  syncError,
}: BillErrorModalProps) => {
  const { t } = useTranslation(['authenticated.bills', 'common'], {
    keyPrefix: 'modals.billError',
  });
  const { t: tGlobal } = useTranslation();
  const error = syncError ?? bill?.syncError ?? null;
  const titleKey =
    syncOperation === BillSyncOperation.Create
      ? 'title.create'
      : syncOperation === BillSyncOperation.Update
        ? 'title.update'
        : 'title.default';
  const reasonKey =
    error?.code === BackendErrorCode.ENTITY_SYNC_INTEGRATION_VALIDATION_ERROR && error.reason
      ? BILL_SYNC_ERROR_REASON_I18N_KEYS[error.reason]
      : null;
  const errorKey = error ? BILL_SYNC_ERROR_I18N_KEYS[error.code] : null;
  const backendKey = error ? BACKEND_ERROR_CODE_TRANSLATIONS[error.code] : null;
  const errorText =
    (reasonKey && t(reasonKey)) ||
    (errorKey && t(errorKey)) ||
    (backendKey && tGlobal(backendKey)) ||
    error?.message ||
    t('fallback');

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  return (
    <ConfirmationModal
      onClose={onClose}
      open={isOpen}
      confirmText={onRetry ? t('retry') : t('close')}
      onConfirm={onRetry ? handleRetry : onClose}
      title={t(titleKey)}
      cancelText={t('cancel')}
      confirmVariant="default"
      hideCloseIcon
      hideCancelButton={!onRetry}
      isLoading={isRetrying}
    >
      <div className="text-sm text-muted-foreground">
        <p>{errorText}</p>
        {error?.details?.length ? (
          <ul className="mt-2 list-inside list-disc">
            {error.details.map((detail, index) => (
              <li key={`${detail}-${index}`}>{detail}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </ConfirmationModal>
  );
};
